// src/pages/ressources/calendrier-rh.tsx
//
// Outil gratuit "Calendrier des échéances RH" pour employeurs / DRH.
// Lead magnet SEO evergreen : les obligations sociales reviennent chaque
// année. Filtrable par mois ET par effectif (une échéance ne s'affiche
// que si elle concerne la taille d'entreprise sélectionnée).
//
// ⚠️ CONTENU DE CONFORMITÉ : chaque échéance porte sa source officielle.
// Un disclaimer visible rappelle que le calendrier est indicatif et ne
// remplace pas un conseil juridique (dates variables selon effectif, date
// de clôture de l'exercice, convention collective).
//
// Sources principales :
//   - service-public.fr / entreprises (obligations employeur)
//   - urssaf.fr (DSN, cotisations)
//   - travail-emploi.gouv.fr (index égalité, entretien professionnel)
//   - PayFit — Calendrier RH 2026
//   - Village de la Justice — Échéances sociales 2026

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarDays,
  Filter,
  Users,
  ExternalLink,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  Repeat,
  ShieldCheck,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// ─── Types ─────────────────────────────────────────────────────────────────

type Category =
  | 'declarations'
  | 'egalite'
  | 'formation'
  | 'entretiens'
  | 'dialogue'
  | 'cloture';

interface Deadline {
  /** Libellé de l'échéance. */
  title: string;
  /** Mois concerné (0-11), ou null si "date anniversaire" propre à l'entreprise. */
  month: number | null;
  /** Date/période affichée (texte libre). */
  when: string;
  /** Effectif minimum concerné (0 = toutes entreprises). */
  minEffectif: number;
  category: Category;
  /** Ce qu'il faut faire / enjeu. */
  detail: string;
  /** Source officielle. */
  sourceLabel: string;
  sourceUrl: string;
  /** true = récurrence non calée sur le calendrier civil (anniversaire salarié / exercice). */
  recurring?: boolean;
}

const CATEGORY_META: Record<Category, { label: string; color: string }> = {
  declarations: { label: 'Déclarations sociales', color: '#2563eb' },
  egalite: { label: 'Égalité & diversité', color: '#d03484' },
  formation: { label: 'Formation & alternance', color: '#7c3aed' },
  entretiens: { label: 'Entretiens', color: '#0891b2' },
  dialogue: { label: 'Dialogue social', color: '#ea580c' },
  cloture: { label: 'Clôture & épargne', color: '#16a34a' },
};

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

// Paliers d'effectif = seuils réglementaires structurants.
const EFFECTIFS = [
  { label: 'Moins de 11', value: 10 },
  { label: '11 à 49', value: 11 },
  { label: '50 à 249', value: 50 },
  { label: '250 et plus', value: 250 },
];

// URLs sources réutilisées.
const SRC = {
  index: {
    label: 'travail-emploi.gouv.fr — Index égalité',
    url: 'https://travail-emploi.gouv.fr/index-egalite-professionnelle-femmes-hommes',
  },
  dsn: {
    label: 'urssaf.fr — DSN',
    url: 'https://www.urssaf.fr/accueil/employeur/declarer-cotiser/comprendre-dsn.html',
  },
  doeth: {
    label: 'service-public.fr — OETH',
    url: 'https://entreprendre.service-public.fr/vosdroits/F1651',
  },
  apprentissage: {
    label: 'service-public.fr — Taxe d’apprentissage',
    url: 'https://entreprendre.service-public.fr/vosdroits/F22574',
  },
  entretienPro: {
    label: 'service-public.fr — Entretien professionnel',
    url: 'https://entreprendre.service-public.fr/vosdroits/F32040',
  },
  nao: {
    label: 'service-public.fr — Négociation annuelle obligatoire',
    url: 'https://entreprendre.service-public.fr/vosdroits/F1697',
  },
  cse: {
    label: 'service-public.fr — CSE',
    url: 'https://entreprendre.service-public.fr/vosdroits/F33808',
  },
  participation: {
    label: 'service-public.fr — Participation',
    url: 'https://entreprendre.service-public.fr/vosdroits/F1699',
  },
  bdese: {
    label: 'service-public.fr — BDESE',
    url: 'https://entreprendre.service-public.fr/vosdroits/F33802',
  },
  conges: {
    label: 'service-public.fr — Congés payés',
    url: 'https://entreprendre.service-public.fr/vosdroits/F32240',
  },
};

// ─── Données ───────────────────────────────────────────────────────────────
// Ordonnées par mois. minEffectif = seuil réglementaire.
const DEADLINES: Deadline[] = [
  // — Récurrent tous les mois —
  {
    title: 'DSN mensuelle',
    month: null,
    when: 'Le 5 (≥ 50 sal.) ou le 15 (< 50 sal.) de chaque mois',
    minEffectif: 0,
    category: 'declarations',
    detail: 'Déclaration sociale nominative : salaires, cotisations, événements (arrêts, fins de contrat). Porte aussi la déclaration OETH mensuelle.',
    sourceLabel: SRC.dsn.label,
    sourceUrl: SRC.dsn.url,
    recurring: true,
  },
  // — Mars —
  {
    title: 'Index égalité professionnelle',
    month: 2,
    when: 'Publication avant le 1er mars',
    minEffectif: 50,
    category: 'egalite',
    detail: "Calcul et publication de l'index (note /100) sur le site de l'entreprise et transmission à l'administration. Sanction jusqu'à 1 % de la masse salariale.",
    sourceLabel: SRC.index.label,
    sourceUrl: SRC.index.url,
  },
  {
    title: 'CSA — contribution alternance',
    month: 2,
    when: 'Via la DSN de mars',
    minEffectif: 250,
    category: 'formation',
    detail: "Contribution supplémentaire à l'apprentissage pour les entreprises de 250+ salariés employant moins de 5 % d'alternants.",
    sourceLabel: SRC.apprentissage.label,
    sourceUrl: SRC.apprentissage.url,
  },
  // — Mai —
  {
    title: 'DOETH — travailleurs handicapés',
    month: 4,
    when: 'Déclaration et versement via la DSN (échéance mai)',
    minEffectif: 20,
    category: 'egalite',
    detail: "Déclaration de l'obligation d'emploi (6 % de l'effectif) et versement de la contribution Agefiph le cas échéant.",
    sourceLabel: SRC.doeth.label,
    sourceUrl: SRC.doeth.url,
  },
  {
    title: 'Solde de la taxe d’apprentissage',
    month: 4,
    when: 'Via la DSN (échéance mai)',
    minEffectif: 0,
    category: 'formation',
    detail: "Versement du solde (0,09 %) de la taxe d'apprentissage, réparti via la plateforme SOLTéA.",
    sourceLabel: SRC.apprentissage.label,
    sourceUrl: SRC.apprentissage.url,
  },
  // — Juin —
  {
    title: 'Fin de la période d’acquisition des congés payés',
    month: 4,
    when: 'Période de référence 1er juin → 31 mai (sauf accord)',
    minEffectif: 0,
    category: 'cloture',
    detail: "Clôture de la période d'acquisition des congés. Nouveau compteur à partir du 1er juin, sauf accord d'entreprise fixant une autre période.",
    sourceLabel: SRC.conges.label,
    sourceUrl: SRC.conges.url,
  },
  {
    title: 'NAO — négociation annuelle obligatoire',
    month: 5,
    when: 'Chaque année (calendrier selon accord de méthode)',
    minEffectif: 50,
    category: 'dialogue',
    detail: "Négociation obligatoire (rémunération, temps de travail, égalité) dans les entreprises dotées d'au moins un délégué syndical.",
    sourceLabel: SRC.nao.label,
    sourceUrl: SRC.nao.url,
  },
  // — Décembre —
  {
    title: 'Versement de la participation',
    month: 11,
    when: 'Au plus tard le dernier jour du 5e mois après la clôture de l’exercice',
    minEffectif: 50,
    category: 'cloture',
    detail: "Obligatoire dans les entreprises de 50+ salariés. Pour un exercice clos au 31/12, versement/blocage avant le 31 mai suivant.",
    sourceLabel: SRC.participation.label,
    sourceUrl: SRC.participation.url,
  },
  {
    title: 'Clôture comptable et sociale',
    month: 11,
    when: 'Décembre (pour un exercice calé sur l’année civile)',
    minEffectif: 0,
    category: 'cloture',
    detail: 'Régularisations de paie, préparation des soldes, DSN de décembre, préparation du calendrier social de l’année suivante.',
    sourceLabel: SRC.dsn.label,
    sourceUrl: SRC.dsn.url,
  },
  // — Récurrences "anniversaire" (month = null) —
  {
    title: 'Entretien professionnel',
    month: null,
    when: 'Tous les 2 ans (date d’embauche) · état des lieux tous les 6 ans',
    minEffectif: 0,
    category: 'entretiens',
    detail: "Distinct de l'entretien annuel d'évaluation. Tous les 6 ans, un état des lieux récapitulatif est obligatoire — son absence peut entraîner un abondement du CPF.",
    sourceLabel: SRC.entretienPro.label,
    sourceUrl: SRC.entretienPro.url,
    recurring: true,
  },
  {
    title: 'BDESE — mise à jour',
    month: null,
    when: 'En continu, à disposition du CSE',
    minEffectif: 50,
    category: 'dialogue',
    detail: 'Base de données économiques, sociales et environnementales tenue à jour et accessible aux représentants du personnel.',
    sourceLabel: SRC.bdese.label,
    sourceUrl: SRC.bdese.url,
    recurring: true,
  },
  {
    title: 'Élections CSE',
    month: null,
    when: 'Tous les 4 ans (renouvellement du mandat)',
    minEffectif: 11,
    category: 'dialogue',
    detail: "Mise en place et renouvellement du comité social et économique dans les entreprises d'au moins 11 salariés (sur 12 mois consécutifs).",
    sourceLabel: SRC.cse.label,
    sourceUrl: SRC.cse.url,
    recurring: true,
  },
  {
    title: 'Entretien annuel d’évaluation',
    month: null,
    when: 'Bonne pratique annuelle (non imposé par la loi)',
    minEffectif: 0,
    category: 'entretiens',
    detail: "Pas d'obligation légale de date, mais souvent prévu par la convention collective ou l'accord d'entreprise. À ne pas confondre avec l'entretien professionnel.",
    sourceLabel: SRC.entretienPro.label,
    sourceUrl: SRC.entretienPro.url,
    recurring: true,
  },
];

// ─── Composant ─────────────────────────────────────────────────────────────

export default function CalendrierRHPage() {
  const [monthFilter, setMonthFilter] = useState<number | 'all'>('all');
  const [effectifFilter, setEffectifFilter] = useState<number>(50);

  const filtered = useMemo(() => {
    return DEADLINES.filter((d) => {
      const effOk = effectifFilter >= d.minEffectif;
      const monthOk =
        monthFilter === 'all' ? true : d.month === monthFilter || d.month === null;
      return effOk && monthOk;
    }).sort((a, b) => {
      if (a.month === null && b.month === null) return 0;
      if (a.month === null) return 1; // les "anniversaire" en fin de liste
      if (b.month === null) return -1;
      return a.month - b.month;
    });
  }, [monthFilter, effectifFilter]);

  const currentEffLabel =
    EFFECTIFS.find((e) => e.value === effectifFilter)?.label ?? '';

  return (
    <Layout
      title="Calendrier des échéances RH 2026 — obligations sociales de l’employeur | recruteurs.io"
      description="Toutes les échéances RH et sociales de l'année, filtrables par mois et par effectif : index égalité, DSN, DOETH, taxe d'apprentissage, entretien professionnel, NAO, CSE, participation. Sources officielles, outil gratuit."
      keywords="calendrier RH, échéances sociales, obligations employeur, index égalité, DSN, DOETH, entretien professionnel, NAO, CSE, calendrier RH 2026"
    >
      <WebPageSchema
        page={{
          title: 'Calendrier des échéances RH — obligations sociales de l’employeur',
          description:
            "Calendrier interactif des obligations RH et sociales, filtrable par mois et par effectif. Index égalité, DSN, DOETH, entretien professionnel, NAO, CSE, participation. Sources officielles.",
          url: 'https://recruteurs.io/ressources/outils/calendrier-rh',
          lastReviewed: '2026-07-27',
        }}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Ressources', url: 'https://recruteurs.io/ressources/articles' },
          { name: 'Outils', url: 'https://recruteurs.io/ressources/outils' },
          {
            name: 'Calendrier RH',
            url: 'https://recruteurs.io/ressources/outils/calendrier-rh',
          },
        ]}
      />

      {/* HERO */}
      <SectionBackground page="outils" section="outils">
        <Section className="pt-24 pb-10">
          <div className="container-custom max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 ring-1 ring-neutral-200 shadow-sm mb-5">
                <Sparkles className="w-4 h-4 text-[#d03484]" />
                <span className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
                  Outil RH · gratuit · sources officielles
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#30475e] leading-[1.05] mb-5">
                Le calendrier des{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  échéances RH
                </span>{' '}
                que vous ne raterez plus.
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 leading-relaxed max-w-3xl mb-2">
                Index égalité, DSN, DOETH, entretien professionnel, NAO, CSE,
                participation… Chaque obligation sociale, à sa date, filtrée selon
                la <strong>taille de votre entreprise</strong>. Avec la source
                officielle pour chacune.
              </p>
            </motion.div>
          </div>
        </Section>
      </SectionBackground>

      {/* DISCLAIMER */}
      <Section className="pb-6">
        <div className="container-custom max-w-5xl">
          <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <p className="text-sm leading-relaxed text-amber-900">
              <strong>Calendrier indicatif.</strong> Les dates exactes varient
              selon votre effectif, la date de clôture de votre exercice et votre
              convention collective. Cet outil ne remplace pas un conseil
              juridique ou l’accompagnement de votre expert-comptable / avocat en
              droit social. Vérifiez toujours l’échéance applicable à votre
              situation via les sources officielles indiquées.
            </p>
          </div>
        </div>
      </Section>

      {/* FILTRES */}
      <Section className="pb-4">
        <div className="container-custom max-w-5xl space-y-4">
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">
              <Users className="h-3.5 w-3.5" /> Effectif de l’entreprise
            </div>
            <div className="flex flex-wrap gap-2">
              {EFFECTIFS.map((e) => (
                <button
                  key={e.value}
                  onClick={() => setEffectifFilter(e.value)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                    effectifFilter === e.value
                      ? 'border-[#d03484] bg-[#d03484] text-white'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#d03484]/50 hover:text-[#d03484]'
                  }`}
                >
                  {e.label} salarié{e.value > 1 ? 's' : ''}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neutral-500">
              <Filter className="h-3.5 w-3.5" /> Mois
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setMonthFilter('all')}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  monthFilter === 'all'
                    ? 'border-[#30475e] bg-[#30475e] text-white'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#30475e]/50'
                }`}
              >
                Toute l’année
              </button>
              {MONTHS.map((m, i) => (
                <button
                  key={m}
                  onClick={() => setMonthFilter(i)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    monthFilter === i
                      ? 'border-[#30475e] bg-[#30475e] text-white'
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#30475e]/50'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* LISTE DES ÉCHÉANCES */}
      <Section className="pb-16">
        <div className="container-custom max-w-5xl">
          <p className="mb-4 text-sm text-neutral-500">
            {filtered.length} échéance{filtered.length > 1 ? 's' : ''} · entreprise{' '}
            <strong className="text-[#30475e]">{currentEffLabel} salariés</strong>
            {monthFilter !== 'all' && (
              <> · <strong className="text-[#30475e]">{MONTHS[monthFilter]}</strong></>
            )}
          </p>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {filtered.map((d, i) => {
              const cat = CATEGORY_META[d.category];
              return (
                <div
                  key={`${d.title}-${i}`}
                  className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition hover:shadow-md"
                  style={{ borderLeft: `4px solid ${cat.color}` }}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: `${cat.color}18`, color: cat.color }}
                    >
                      {cat.label}
                    </span>
                    {d.recurring && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-neutral-400"
                        title="Récurrence propre à l’entreprise (date anniversaire ou exercice)"
                      >
                        <Repeat className="h-3 w-3" /> récurrent
                      </span>
                    )}
                  </div>
                  <h3 className="text-base font-bold text-[#30475e]">{d.title}</h3>
                  <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-[#d03484]">
                    <CalendarDays className="h-3.5 w-3.5" /> {d.when}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">
                    {d.detail}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
                      <Users className="h-3 w-3" />
                      {d.minEffectif === 0
                        ? 'Toutes entreprises'
                        : `≥ ${d.minEffectif} salariés`}
                    </span>
                    <a
                      href={d.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-[#d03484] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" /> {d.sourceLabel}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-400">
              Aucune échéance pour ce filtre. Élargissez la sélection.
            </div>
          )}
        </div>
      </Section>

      {/* CTA */}
      <Section className="pb-24">
        <div className="container-custom max-w-3xl">
          <div className="rounded-3xl bg-gradient-to-br from-[#30475e] to-[#3d5a75] p-8 text-center text-white md:p-12">
            <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-white/90" />
            <h2 className="text-2xl font-bold md:text-3xl">
              Le recrutement fait aussi partie de vos échéances.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-white/80">
              Anticiper ses obligations sociales, c’est bien. Sécuriser ses
              recrutements pour ne pas les subir dans l’urgence, c’est mieux.
              Parlons de vos enjeux RH de l’année.
            </p>
            <a
              href="/reservation"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:scale-[1.02]"
            >
              Planifier un échange
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <p className="mt-6 text-center text-xs text-neutral-400">
            Sources : service-public.fr · urssaf.fr · travail-emploi.gouv.fr ·
            PayFit (Calendrier RH 2026) · Village de la Justice (Échéances
            sociales 2026). Dernière revue : juillet 2026.
          </p>
        </div>
      </Section>
    </Layout>
  );
}
